import {
  ChangeDetectionStrategy, Component, Inject, input, model, PLATFORM_ID, signal, Signal, viewChild, ViewEncapsulation
} from '@angular/core';

import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, switchMap, take, filter, Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink } from '@angular/router';

import { CmsService } from '@spartacus/core';

import {
  BrandsEnum, IMedia, SdBannerComponent, SdButtonComponent, SdFormErrorComponent, SdFormInputComponent,
  SdHtmlComponent, SdLoaderComponent, SdModalDefaultComponent, SdTitleComponent, StatusEnum
} from '@seb/web-design-system/core';

import { AbandonedCartComponentInterface, IAbandonedCartPopin } from '../../../../models/cms/abandoned-cart.interface';
import { CaptchaTurnstileComponent } from '../captcha-turnstile/captcha-turnstile.component';
import { CustomPatternValidator } from '../../../../constants/validators';
import { isEmailValid, isValidEmailFormat } from '../../../../utils';
import { InputTypeEnum } from '../../../../enums';

import {
  AbandonedCartTimerService, CookiesConsentService, BaseStoreService, CartFacadeService,
  DqeService, ToasterService, TrackingService
} from '../../../../services';

@Component({
  selector: 'wc-popin-abandoned-cart',
  templateUrl: './popin-abandoned-cart.component.html',
  styleUrls: ['./popin-abandoned-cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    SdModalDefaultComponent,
    SdBannerComponent,
    CaptchaTurnstileComponent,
    TranslateModule,
    SdFormErrorComponent,
    SdTitleComponent,
    SdLoaderComponent,
    SdButtonComponent,
    ReactiveFormsModule,
    SdFormInputComponent,
    SdHtmlComponent,
    RouterLink
  ]
})
export class PopinAbandonedCartComponent {
  brand = input<string>(BrandsEnum.default);
  isOpened = model(false);

  recaptcha = viewChild(CaptchaTurnstileComponent);

  abandonedCartPoppingData?: Signal<IAbandonedCartPopin>;
  suggestions = signal<{ value: string }[]>([]);
  isLoading = signal(false);

  private isDisplayAbandonedCartEventSent = false;
  private isClosedAbandonedCartEventSent = false;
  private subscriptions = new Subscription();
  private userEmail: string;
  private typingTimer: any;

  abandonedCartForm = new FormGroup({
    email: new FormControl('', [
      Validators.pattern(CustomPatternValidator.email),
      Validators.minLength(4)
    ])
  });

  protected readonly InputTypeEnum = InputTypeEnum;

  get email() {
    return this.abandonedCartForm.get('email') as FormControl;
  }

  constructor(
    private readonly baseStoreService: BaseStoreService,
    private readonly cmsService: CmsService,
    private readonly translateService: TranslateService,
    private readonly dqeService: DqeService,
    private readonly toasterService: ToasterService,
    private readonly cartFacadeService: CartFacadeService,
    private readonly trackingService: TrackingService,
    private readonly timerService: AbandonedCartTimerService,
    private readonly cookiesConsentService: CookiesConsentService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    if (isPlatformBrowser(this.platformId)) {
      if (this.baseStoreService.baseStore.displayAbandonedCartPopin) {
        this.initializeComponent();

        this.subscriptions.add(
          this.timerService.isDisplayPopin$.subscribe((isDisplayPopin: boolean) => {
            if (isDisplayPopin && this.cookiesConsentService.hasAcceptedCookies()) {
              this.openModal();
            }
          })
        );
      }
    }
  }

  private initializeComponent(): void {
    this.abandonedCartPoppingData = toSignal<IAbandonedCartPopin>(
      this.cmsService.getContentSlot('AbandonedCartPopin').pipe(
        filter(({ components }) => (
          !!components?.length
        )),
        switchMap(({ components }) => (
          this.cmsService.getComponentData(components[0].uid)
        )),
        take(1),
        filter((popinData: AbandonedCartComponentInterface) => (
          !!popinData
        )),
        map((popinData) => ({
          useDataQualityFormValidation: this.baseStoreService.baseStore.enableDQE,
          emailPlaceHolder: popinData.emailPlaceHolder,
          legalMentions: popinData.legalMentions,
          description: popinData.description,
          media: popinData.media as IMedia,
          emailLabel: popinData.emailLabel,
          sendAction: popinData.sendAction,
          title: popinData.title
        }))
      )
    );
  }

  private openModal(): void {
    this.isOpened.set(true);

    if (!this.isDisplayAbandonedCartEventSent) {
      this.trackingService.sendEvent('abandonedCartDisplay', {});
      this.isDisplayAbandonedCartEventSent = true;
    }
  }

  onSubmit(): void {
    if (this.abandonedCartForm.valid) {
      const { email } = this.abandonedCartForm.value;
      this.onSubmitAbandonedCartEmail(email);
    } else {
      this.abandonedCartForm.markAllAsTouched();
    }
  }

  onSubmitAbandonedCartEmail(userEmail: string): void {
    this.userEmail = userEmail;
    this.isLoading.set(true);
    this.recaptcha()?.execute();
    this.trackingService.sendEvent('abandonedCartSubmit', {});
  }

  onInputChange(): void {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer);
    }
  }

  onCloseModal(): void {
    this.isOpened.set(false);
    this.isLoading.set(false);
  }

  // Event handlers
  saveClosed(_event: any): void {
    if (!this.isOpened() && !this.isClosedAbandonedCartEventSent) {
      this.trackingService.sendEvent('abandonedCartClose', {});
      this.isClosedAbandonedCartEventSent = true;
    }
  }

  recaptchaValidated(): void {
    this.subscriptions.add(
      this.cartFacadeService.submitAbandonedCartEmail(this.userEmail).subscribe({
        next: () => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.success,
            title: this.translateService.instant('abandoned.cart.success.title'),
            description: this.translateService.instant('abandoned.cart.success.description')
          });
          this.onCloseModal();
        },
        error: () => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.error,
            title: 'notify.notifyErrorToastTitle',
            description: 'httpErrors.500'
          });
        }
      })
    );
  }

  captchaError(): void {
    this.isLoading.set(false);
    this.toasterService.sendToasterEvent({
      status: StatusEnum.error,
      title: 'notify.notifyErrorToastTitle',
      description: 'httpErrors.500'
    });
  }

  loadSuggestions(input: string): void {
    this.onInputChange();

    if (isPlatformBrowser(this.platformId)) {
      this.typingTimer = setTimeout(() => {
        this.typingTimer = undefined;
        if (input.includes('@')) {
          this.subscriptions.add(
            this.dqeService.suggest(input).subscribe((result) => {
              this.suggestions.set(result['suggest'].map((key: string) => ({
                value: key
              })));
            })
          );
        }
      }, 600);
    }
  }

  validateInput(type?: InputTypeEnum): boolean {
    if (!isValidEmailFormat(this.email?.value)) {
      return false;
    }

    this.subscriptions.add(
      this.dqeService.validate(this.email?.value, type).subscribe({
        next: (emailDataEligibility) => {
          if (!isEmailValid(emailDataEligibility)) {
            const errorObject = Object.values(emailDataEligibility).find(
              (obj) => obj.IdError
            );

            this.email.setErrors({
              pattern: true,
              idError:
                errorObject?.IdError === '92'
                  ? `${errorObject.Redressement}_${errorObject.IdError}`
                  : errorObject?.IdError,
              suggestedEmail:
                errorObject?.Redressement === 1 ? errorObject.eMail : ''
            });

            return false;
          } else {
            const errors = this.email.errors;

            if (errors?.pattern) {
              // TODO-EL: ne pas modifier les variables gérées en interne
              // dans le FormGroup ou le FormControl
              delete errors.pattern;
              delete errors.idError;

              this.email.setErrors(Object.keys(errors).length ? errors : null);
              this.email.updateValueAndValidity();
              return true;
            }

            this.email.updateValueAndValidity();
          }

          return false;
        },
        error: (_error) => {
          this.toasterService.sendToasterEvent({
            status: StatusEnum.error,
            title: 'notify.notifyErrorToastTitle',
            description: 'httpErrors.500'
          });
        }
      })
    );

    return true;
  }
}
